<template>
	<div class="mt-4">
		<app-header-teleport>
			<app-title :title="$t('top_products')" />
			<div class="ml-2">
				<app-advanced-filter @onFilter="onFilter" @onClear="onClear" :loading="isLoading">
					<app-advanced-date-filter v-model="filters" :start-date="'startDate'" :end-date="'endDate'" />
					<app-select
						:options="paymentTypes"
						class="w-48"
						:placeholder="`${$t('paymentTypes')}`"
						:can-deselect="false"
						v-model="filters.paymentType"
					/>
				</app-advanced-filter>
			</div>
		</app-header-teleport>
		<app-section>
			<app-table :columns="columns" :data="data">
				<template v-slot:totalValue="{ item }">
					<span>{{ $filters.addCommas(item.totalValue) }}</span>
				</template>
				<template v-slot:avgValue="{ item }">
					<span>{{
						item.totalQuantity === 0
							? 0
							: $filters.addCommas((item.totalValue / item.totalQuantity).toFixed(1))
					}}</span>
				</template>
				<template v-slot:percentageWithPreviousPeriod="{ item }">
					<p class="text-13" :class="item.percentageWithPreviousPeriod > 0 ? 'text-success' : 'text-primary'">
						<app-icon
							:icon="item.percentageWithPreviousPeriod > 0 ? 'sort-up' : 'sort-down'"
							:class="item.percentageWithPreviousPeriod > 0 ? 'text-success' : 'text-primary'"
						/>
						{{
							item.percentageWithPreviousPeriod === 0
								? 0
								: (item.percentageWithPreviousPeriod / 100).toFixed(2)
						}}%
					</p>
				</template>
			</app-table>
			<app-pagination
				:filters="filters"
				@prevPage="prevPage"
				@nextPage="nextPage"
				@loadMore="loadMore"
				:length="data.length"
				@limitChange="changeLimit"
			/>
		</app-section>
	</div>
</template>

<script>
	import { mapGetters } from 'vuex';
	import AppTable from '@/components/app/AppTable';
	import ReportService from '@/services/ReportService';
	import AppAdvancedFilter from '@/components/app/AppAdvancedFilter';
	import AppAdvancedDateFilter from '@/components/app/AppAdvancedDateFilter';
	import constants from '@/constants/finance';
	import moment from 'moment';
	import CommonService from '@/services/CommonService';
	import AppSelect from '@/components/app/AppSelect.vue';
	import AppPagination from '@/components/app/AppPagination.vue';

	const defaultFilters = {
		startDate: moment()
			.add(-1, 'day')
			.format('YYYY-MM-DD'),
		endDate: moment().format('YYYY-MM-DD'),
		limit: 50,
		offset: 0,
		paymentType: -1,
	};

	export default {
		name: 'TopProducts',
		components: {
			AppPagination,
			AppSelect,
			AppTable,
			AppAdvancedFilter,
			AppAdvancedDateFilter,
		},
		computed: {
			...mapGetters({
				user: 'user',
			}),
			businessId() {
				if (this.$route.params.id) {
					return this.$route.params.id;
				}
				return this.user.restaurants[0].restaurantID;
			},
		},
		data: () => ({
			filters: { ...defaultFilters },
			searchFilters: { ...defaultFilters },
			data: [],
			paymentTypes: [],
			isLoading: false,
			columns: [
				{
					isSortable: true,
					key: 'itemName',
					label: 'itemName',
				},
				{
					key: 'categoryName',
					label: 'categoryName',
				},
				{
					isSortable: true,
					key: 'totalQuantity',
					label: 'totalQuantity',
					initialSort: true,
					initialSortDirection: 'desc',
				},
				{
					isSortable: false,
					key: 'avgValue',
					label: 'avgValue',
				},
				{
					isSortable: true,
					key: 'totalValue',
					label: 'totalValue',
				},
				{
					isSortable: true,
					key: 'percentageWithPreviousPeriod',
					label: 'growthPercentage',
				},
			],
		}),
		methods: {
			init() {
				this.getSalesByItem();
			},
			getSalesByItem(loadMore = false) {
				this.isLoading = true;
				ReportService.salesByItem({
					...this.searchFilters,
					businessIDs: this.businessId,
				})
					.then((data) => {
						if (loadMore) {
							if (!data.length) this.$notificationService.info('No more items to load');
							this.data = this.data.concat(data);
						} else this.data = data;
					})
					.catch((error) => {
						this.$notificationService.error(error);
					})
					.finally(() => {
						this.isLoading = false;
					});
			},
			getPaymentTypes() {
				CommonService.getPaymentTypes(this.businessId).then((data) => {
					this.paymentTypes = data;
				});
			},
			onFilter() {
				this.searchFilters = { ...this.filters };
				this.init();
			},
			onClear() {
				this.filters = { ...constants.defaultFilters };
				this.searchFilters = { ...constants.defaultFilters };
				this.init();
			},
			prevPage() {
				this.searchFilters.offset = Math.max(this.searchFilters.offset - this.searchFilters.limit, 0);
				this.filters = { ...this.searchFilters };
				this.init();
			},
			nextPage() {
				this.searchFilters.offset = this.searchFilters.offset + this.searchFilters.limit;
				this.filters = { ...this.searchFilters };
				this.init();
			},
			loadMore() {
				this.searchFilters.offset = this.searchFilters.offset + this.searchFilters.limit;
				this.filters = { ...this.searchFilters };
				this.getSalesByItem(true);
			},
			changeLimit(value) {
				this.searchFilters = {
					...this.searchFilters,
					limit: value,
					offset: 0,
				};
				this.init();
			},
		},
		mounted() {
			this.getPaymentTypes();
			this.init();
		},
	};
</script>
